import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, Loader2, Check, Ban, Trash2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from './adminConstants';

const AdminCouponsTab = () => {
  const { getToken } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCoupon, setNewCoupon] = useState({ code: '', discount_type: 'percentage', discount_value: '', max_uses: '', expires_at: '' });
  const [creating, setCreating] = useState(false);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API}/admin/coupons`, { headers: { Authorization: `Bearer ${token}` } });
      setCoupons(res.data);
    } catch { toast.error('Erro ao carregar cupons'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const createCoupon = async () => {
    if (!newCoupon.code || !newCoupon.discount_value) { toast.error('Preencha código e valor'); return; }
    setCreating(true);
    try {
      const token = getToken();
      await axios.post(`${API}/admin/coupons`, {
        ...newCoupon, discount_value: parseFloat(newCoupon.discount_value),
        max_uses: newCoupon.max_uses ? parseInt(newCoupon.max_uses) : null,
        expires_at: newCoupon.expires_at || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cupom criado!');
      setNewCoupon({ code: '', discount_type: 'percentage', discount_value: '', max_uses: '', expires_at: '' });
      fetchCoupons();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao criar cupom'); }
    finally { setCreating(false); }
  };

  const toggleCoupon = async (id) => {
    try {
      const token = getToken();
      await axios.put(`${API}/admin/coupons/${id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchCoupons();
    } catch { toast.error('Erro ao alterar cupom'); }
  };

  const deleteCoupon = async (id) => {
    try {
      const token = getToken();
      await axios.delete(`${API}/admin/coupons/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cupom excluido');
      fetchCoupons();
    } catch { toast.error('Erro ao excluir cupom'); }
  };

  return (
    <div className="space-y-6" data-testid="cupons-section">
      <Card className="border-0 shadow-sm border-l-4 border-l-purple-500">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><CreditCard className="w-5 h-5 text-purple-500" />Criar Novo Cupom</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Código</label><input value={newCoupon.code} onChange={e => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})} placeholder="FILIAL30" className="w-full border rounded-lg px-3 py-2 text-sm font-mono uppercase" data-testid="coupon-code-input" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Tipo</label><select value={newCoupon.discount_type} onChange={e => setNewCoupon({...newCoupon, discount_type: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="coupon-type-select"><option value="percentage">Percentual (%)</option><option value="fixed">Fixo (R$)</option></select></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Valor</label><input type="number" value={newCoupon.discount_value} onChange={e => setNewCoupon({...newCoupon, discount_value: e.target.value})} placeholder={newCoupon.discount_type === 'percentage' ? '30' : '15.00'} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="coupon-value-input" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Usos Max (opcional)</label><input type="number" value={newCoupon.max_uses} onChange={e => setNewCoupon({...newCoupon, max_uses: e.target.value})} placeholder="Ilimitado" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="coupon-max-uses-input" /></div>
            <div className="flex items-end"><Button onClick={createCoupon} disabled={creating} className="w-full bg-purple-600 hover:bg-purple-700 text-white h-10" data-testid="create-coupon-btn">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Criar Cupom'}</Button></div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-base">Cupons Cadastrados</CardTitle></CardHeader>
        <CardContent>
          {loading ? <div className="flex justify-center py-8"><div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" /></div>
          : coupons.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="coupons-table">
                <thead><tr className="border-b text-slate-500 text-xs"><th className="text-left px-4 py-3">Código</th><th className="text-center px-4 py-3">Tipo</th><th className="text-center px-4 py-3">Valor</th><th className="text-center px-4 py-3">Usos</th><th className="text-center px-4 py-3">Status</th><th className="text-center px-4 py-3">Acoes</th></tr></thead>
                <tbody>
                  {coupons.map(c => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono font-bold text-purple-700">{c.code}</td>
                      <td className="px-4 py-3 text-center text-xs">{c.discount_type === 'percentage' ? 'Percentual' : 'Fixo'}</td>
                      <td className="px-4 py-3 text-center font-bold">{c.discount_type === 'percentage' ? `${c.discount_value}%` : `R$ ${c.discount_value.toFixed(2)}`}</td>
                      <td className="px-4 py-3 text-center text-xs">{c.used_count}{c.max_uses ? `/${c.max_uses}` : '/ilim.'}</td>
                      <td className="px-4 py-3 text-center"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${c.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>{c.active ? 'Ativo' : 'Inativo'}</span></td>
                      <td className="px-4 py-3 text-center"><div className="flex gap-1 justify-center">
                        <button onClick={() => toggleCoupon(c.id)} className={`p-1.5 rounded-lg transition-colors ${c.active ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}>{c.active ? <Ban className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}</button>
                        <button onClick={() => deleteCoupon(c.id)} className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-center text-slate-400 py-8 text-sm">Nenhum cupom cadastrado.</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCouponsTab;
